/**
 * **Shared mobile menu**
 * Puts the left-sidebar product list into the header drawer and lets
 * both the header hamburger and the Products pill open that same drawer.
 */
(function initIbCombinedMobileMenu() {
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  /**
   * Finds the header drawer custom element.
   * @returns {HTMLElement | null}
   */
  function getHeaderDrawer() {
    return document.querySelector('header-drawer');
  }

  /**
   * Finds the details element that actually opens the header drawer.
   * @param {HTMLElement | null} drawer
   * @returns {HTMLDetailsElement | null}
   */
  function getDrawerDetails(drawer) {
    if (!drawer) return null;
    return drawer.querySelector('#Details-menu-drawer-container, [ref="details"]');
  }

  /**
   * Copies the sidebar product nav into the header drawer.
   * Runs again when the drawer opens so late hydration cannot empty it.
   */
  function syncSidebarMenuIntoDrawer() {
    const slot = document.querySelector('[data-ib-mobile-combined-menu]');
    const nav = document.querySelector('[data-ib-sidebar-nav]');
    if (!slot || !nav) return false;

    if (slot.dataset.ibCombinedSynced === 'true' && slot.childElementCount > 0) {
      slot.hidden = false;
      return true;
    }

    slot.replaceChildren();

    const heading = document.querySelector('[data-ib-sidebar-heading]');
    if (heading && heading.textContent.trim()) {
      const title = document.createElement('h4');
      title.className = 'ib-mobile-combined-menu__heading ib-left-sidebar__heading';
      title.textContent = heading.textContent.trim();
      slot.appendChild(title);
    }

    slot.appendChild(nav.cloneNode(true));
    slot.hidden = false;
    slot.dataset.ibCombinedSynced = 'true';
    return true;
  }

  /**
   * Opens the header drawer so both mobile icons share one menu.
   */
  function openSharedMobileMenu() {
    const drawer = getHeaderDrawer();
    const details = getDrawerDetails(drawer);
    if (!drawer || !details) return;

    syncSidebarMenuIntoDrawer();

    if (!details.hasAttribute('open')) {
      details.setAttribute('open', '');
    }

    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      details.classList.add('menu-open');
    }

    syncProductsButtonState(true);
  }

  /**
   * Closes the shared header drawer.
   */
  function closeSharedMobileMenu() {
    const drawer = getHeaderDrawer();
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    } else {
      const details = getDrawerDetails(drawer);
      details?.classList.remove('menu-open');
      details?.removeAttribute('open');
    }

    syncProductsButtonState(false);
  }

  /**
   * Opens or closes the shared menu, matching the header hamburger.
   */
  function toggleSharedMobileMenu() {
    const details = getDrawerDetails(getHeaderDrawer());
    if (!details) return;

    if (details.hasAttribute('open') && details.classList.contains('menu-open')) {
      closeSharedMobileMenu();
      return;
    }

    openSharedMobileMenu();
  }

  /**
   * Keeps the Products pill in sync with the open drawer.
   * @param {boolean} [isOpen]
   */
  function syncProductsButtonState(isOpen) {
    const details = getDrawerDetails(getHeaderDrawer());
    const open = typeof isOpen === 'boolean' ? isOpen : !!(details?.hasAttribute('open') && details.classList.contains('menu-open'));

    document.querySelectorAll('[data-ib-sidebar-open]').forEach(function syncButton(button) {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  window.ibToggleSharedMobileMenu = toggleSharedMobileMenu;
  window.ibOpenSharedMobileMenu = openSharedMobileMenu;
  window.ibCloseSharedMobileMenu = closeSharedMobileMenu;

  function bind() {
    if (desktopQuery.matches) {
      const slot = document.querySelector('[data-ib-mobile-combined-menu]');
      if (slot) {
        slot.replaceChildren();
        slot.hidden = true;
        delete slot.dataset.ibCombinedSynced;
      }
      syncProductsButtonState(false);
      return;
    }

    syncSidebarMenuIntoDrawer();

    const details = getDrawerDetails(getHeaderDrawer());
    if (details && !details.dataset.ibCombinedMenuBound) {
      details.dataset.ibCombinedMenuBound = 'true';
      const observer = new MutationObserver(function onDrawerChange() {
        if (details.hasAttribute('open') || details.classList.contains('menu-open')) {
          syncSidebarMenuIntoDrawer();
        }
        syncProductsButtonState();
      });
      observer.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });
    }

    syncProductsButtonState();
  }

  bind();
  desktopQuery.addEventListener('change', bind);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  }
  window.addEventListener('load', bind);
})();
